import { generateUUID, isDefined, isObject, scopedErrorMessage } from '@philips-internal/oswald-ui-utils';
import { checkDuplicatedUUIDS } from '../utils/index.js';
const DUMMY_ROOT = 'DUMMY_ROOT';
function createDummyNode() {
    return { children: [], props: { uuid: DUMMY_ROOT }, type: 'Container' };
}
/**
 * Handler to Fragment's metadata.
 *
 * Responsible to manage operations on metadata.
 */
class FragmentMetadata {
    constructor(name, content) {
        this.root = createDummyNode();
        this.componentMap = new Map();
        this.parentBy = new Map(); // childUUID, parentUUID
        this.createNode = (node) => {
            const { children, props, type } = node;
            props.uuid = props.uuid ?? generateUUID();
            return {
                children: children?.map(this.createNode),
                props,
                type,
            };
        };
        this.addChildNode = (parent, node, fillParent = true, toIndex) => {
            const isRoot = parent === DUMMY_ROOT;
            const isToIndex = isDefined(toIndex);
            this.root = isRoot ? node : this.root;
            const { uuid } = node.props;
            this.componentMap.set(uuid, node);
            this.parentBy.set(uuid, parent);
            if (isToIndex) {
                const parentNode = this.componentMap.get(parent);
                if (parentNode && parentNode.children) {
                    parentNode.children.splice(toIndex, 0, node);
                }
            }
            if (!isToIndex && fillParent && this.componentMap.has(parent)) {
                const parentNode = this.componentMap.get(parent);
                if (parentNode) {
                    if (!parentNode.children) {
                        parentNode.children = [];
                    }
                    parentNode.children.push(node);
                }
            }
            if (node.children) {
                node.children.forEach(childNode => (this.addChildNode(uuid, childNode, false)));
            }
        };
        this.reset();
        this.name = name;
        content && this.load(content);
    }
    getName() {
        return this.name;
    }
    reset() {
        this.root = createDummyNode();
        this.componentMap = new Map();
        this.componentMap.set(DUMMY_ROOT, this.root);
        this.parentBy = new Map();
    }
    load(content) {
        this.addChildNode(DUMMY_ROOT, content, false);
        const root = this.componentMap.get(DUMMY_ROOT);
        if (root && root.children) {
            root.children.push(content);
        }
    }
    getRoot() {
        return this.root;
    }
    getNode(uuid) {
        return this.componentMap.get(uuid) || null;
    }
    getTypedNode(uuid) {
        const node = this.componentMap.get(uuid);
        if (node) {
            return node;
        }
        return null;
    }
    getParent(uuid) {
        return this.parentBy.get(uuid) || '';
    }
    updateNode(uuid, props = {}) {
        const component = this.componentMap.get(uuid);
        if (component) {
            Object.assign(component.props, props);
            this.cleanNode(component.props);
        }
    }
    cleanNode(item) {
        if (isObject(item)) {
            this.removeNullItems(item);
        }
    }
    removeNullItems(item) {
        Object.keys(item).forEach(key => {
            if (item[key] === null) {
                delete item[key];
            }
            else {
                this.cleanNode(item[key]);
            }
        });
    }
    deleteNode(uuid) {
        const component = this.componentMap.get(uuid);
        if (!component)
            return;
        if (this.root === component) {
            this.reset();
        }
        else {
            const { children } = component;
            if (children) {
                children.forEach(child => this.deleteNode(child.props.uuid));
            }
            const parentUuid = this.parentBy.get(uuid) || '';
            const parent = this.getNode(parentUuid);
            if (parent && parent.children) {
                parent.children = parent.children.filter(child => child.props.uuid !== uuid);
            }
            this.componentMap.delete(uuid);
        }
    }
    validate() {
        const root = this.getRoot();
        try {
            checkDuplicatedUUIDS(root);
        }
        catch (e) {
            scopedErrorMessage('fragment-metadata', e);
            return false;
        }
        return true;
    }
}
FragmentMetadata.DUMMY_ROOT = DUMMY_ROOT;
export { FragmentMetadata, };
//# sourceMappingURL=fragment-metadata.js.map